import { MouseEvent, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { batch, useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import { SxProps, Theme } from "@mui/system";
import { Box, Button, Stack, Typography } from "@mui/material";
import NGIcon from "../NGIcon/NGIcon";
import NGImage from "../NGImage/NGImage";
import { INGDocumentViewerProps } from "../../library/NGFieldExtensions";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { getTestId, getsxObject, getClassName, generateUID } from "../../library/utils";
import { isNil, isString, toNumber } from "lodash-es";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { log } from "../../library/logger";
import useResizeObserver from "@react-hook/resize-observer";
import { validateCookiesForUrl } from "../../library/cookieValidation";

import type { DocumentCallback } from "react-pdf/dist/esm/shared/types.js";
import { Icon } from "../../../resolvers-types";
import DocumentLoader from "./DocumentLoader";

const defaultStyle = (width = "fit-content") => ({
  width,
  position: "relative",
  "& .react-pdf__Page": {
    width,
  },
  "& .react-pdf__Document": {
    width,
  },
  display: "flex",
  flexDirection: "row",
});

const tag = "NGDocumentViewer";

const useWidth = (target: RefObject<HTMLDivElement>, previewMode: boolean) => {
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!target.current) return;
    setWidth(target.current.getBoundingClientRect().width);
  }, [target]);

  useResizeObserver(target, (entry) => {
    if (previewMode) return;
    setWidth(entry.contentRect?.width);
  });
  return width;
};

export default function NGDocumentViewer({ config, context }: INGDocumentViewerProps) {
  const local = setupLocalState(
    config,
    {
      NumPages: useSignal(0), // Initialize with 0, updated on document load
      CurrentPage: useSignal(1), // Signal to track the current page
      CurrentDocument: useSignal(-1), // Signal to track the current document
      PagesRendered: useSignal(null),
      PreviewMode: useSignal(config.PreviewMode ?? false),
      Scale: useSignal(config.Scale ?? 1),
      Visible: useSignal(config.Visible != false ? true : config.Visible),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Value: useSignal(config.Value ?? ""),
      Documents: useSignal(config.Documents ?? []),
      ButtonStyle: useSignal(config.ButtonStyle ?? {}),
      ButtonClasses: useSignal(config.ButtonClasses ?? []),
      AuxiliaryButtonClasses: useSignal(config.AuxiliaryButtonClasses ?? []),
      AuxiliaryButtonStyle: useSignal(config.AuxiliaryButtonStyle ?? {}),
      DocumentFormat: useSignal(config.DocumentFormat ?? "pdf"),
      ErrorMessage: useSignal(config.ErrorMessage ?? "Preview Unavailable"),
      MaxLoadingTimeSeconds: useSignal(config.MaxLoadingTimeSeconds ?? 60),
      PathExpression: useSignal(config.PathExpression ?? ""),
      ValueExpression: useSignal(config.ValueExpression ?? ""),
      InfiniteMode: useSignal(config.InfiniteMode ?? false),
      NavigationButtonSize: useSignal(config.NavigationButtonSize ?? "42px !important"),
      LoadAllPage: useSignal(config?.LoadAllPage ?? false),
    },
    context
  );
  const paginationRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const handlers = setupHandlers(config, context);
  const { width } = useWindowWidth();
  const loadingInterval = useSignal<number | null>(null);
  const retryCount = useSignal(0);
  const maxRetries = 3;
  const isRetrying = useSignal(false);

  const documentModule = useSignal<any>(null);
  const pageModule = useSignal<any>(null);
  useEffect(() => {
    const loadModules = async () => {
      await import("react-pdf/dist/Page/AnnotationLayer.css");
      await import("react-pdf/dist/Page/TextLayer.css");
      const { Document, Page, pdfjs } = await import("react-pdf");
      const workerUrl = await import("pdfjs-dist/build/pdf.worker.min.mjs");
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(workerUrl.default, import.meta.url).toString();

      documentModule.value = Document;
      pageModule.value = Page;
    };

    loadModules();
  }, []);

  const Document = documentModule.value;
  const Page = pageModule.value;

  function handleOnClick(event: MouseEvent<HTMLDivElement>) {
    if (!isNil(handlers["onClick"])) handlers["onClick"](new Event("click"), event);
  }

  function onDocumentLoadSuccess(pdf: DocumentCallback) {
    const { numPages } = pdf;
    log.info(tag, "Document loaded successfully");
    batch(() => {
      local.NumPages.value = numPages; // Set the total number of pages once the document is loaded
      local.CurrentPage.value = 1; // Reset the current page to 1
      loadingInterval.value = null;
      local.PagesRendered.value = 0;
      retryCount.value = 0; // Reset retry count on successful load
      isRetrying.value = false;
    });
  }

  function onDocumentError(error: Error) {
    log.error(tag, `Error loading document (attempt ${retryCount.value + 1}/${maxRetries + 1})`, error);

    if (retryCount.value < maxRetries) {
      // Set retrying state immediately
      isRetrying.value = true;

      // Calculate exponential backoff delay: 1s, 2s, 4s for attempts 1, 2, 3
      const retryDelay = Math.pow(2, retryCount.value) * 1000;

      // Wait for the backoff delay before incrementing retry count and attempting retry
      setTimeout(() => {
        batch(() => {
          retryCount.value += 1;
        });

        log.info(tag, `Retrying document load (attempt ${retryCount.value}/${maxRetries})`);
        isRetrying.value = false;
        // The Document component will automatically retry when isRetrying changes
      }, retryDelay);
    } else {
      // Max retries exceeded, show error
      log.error(tag, "Max retries exceeded, showing error message");
      batch(() => {
        local.NumPages.value = 0;
        local.CurrentPage.value = 1;
        loadingInterval.value = null;
        isRetrying.value = false;
      });
    }
  }

  function goToPrevPage() {
    if (local.CurrentPage.value > 1) {
      local.CurrentPage.value -= 1;
    }
  }

  function goToNextPage() {
    if (local.CurrentPage.value < local.NumPages.value) {
      local.CurrentPage.value += 1;
    }
  }

  function setDocument(index: number) {
    if (local.Value.value !== null) local.Value.value = local.Documents.value[index];
  }

  function goToNextDocument() {
    if (local.CurrentDocument.value < local.Documents.value.length - 1) {
      local.CurrentDocument.value += 1;
      setDocument(local.CurrentDocument.value);
    }
  }

  function goToPrevDocument() {
    if (local.CurrentDocument.value > 0) {
      local.CurrentDocument.value -= 1;
      setDocument(local.CurrentDocument.value);
    }
  }

  function zoomIn() {
    if (local.Scale.value <= 5) {
      local.Scale.value += 0.1;
    }
  }

  function zoomOut() {
    if (local.Scale.value >= 0.3) {
      local.Scale.value -= 0.1;
    }
  }

  const selected = useComputed(() => {
    const vex = local.ValueExpression.value;
    const pex = local.PathExpression.value;
    if (isString(local.Value.value) && local.Value.value) return local.Value.value;
    if (!vex) return "";

    if (!local.Documents.value || local.Documents.value.length === 0) {
      if (!local.Value.value) return "";
      // value is object
      // Extract the value expression and path expression
      const valueExpression = local.Value.value?.[vex];
      // Combine the path expression and value expression
      return `${pex}${valueExpression}`;
    }
    if (!local.Value.value) {
      setDocument(0);
    }
    // get the index of the selected document
    const selectedDocumentIndex = local.Documents.value.findIndex((doc) => doc?.[vex] === local.Value.value?.[vex]);
    // if selected document is not found
    if (selectedDocumentIndex === -1 && local.Documents.value.length > 0) {
      if (local.CurrentDocument.value > 0) {
        // if the current document is not the first document
        // set previous document as the selected document
        goToPrevDocument();
        return;
      }
      // set the first document as the selected document
      setDocument(0);
      return;
    }
    // document found
    // when selected document is not the current document
    const selectedDocument = local.Documents.value[selectedDocumentIndex];
    local.CurrentDocument.value = selectedDocumentIndex;
    return `${pex}${selectedDocument?.[vex]}`;
  });

  // Validate cookies for the document URL before rendering
  const cookieValidated = useSignal(false);

  useSignalEffect(() => {
    const validateDocument = async () => {
      if (!selected.value) {
        cookieValidated.value = true;
        return;
      }

      try {
        await validateCookiesForUrl(selected.value);
        cookieValidated.value = true;
      } catch (error) {
        log.error(tag, "Cookie validation failed for document:", selected.value, error);
        cookieValidated.value = false;
      }
    };

    validateDocument();
  });

  useSignalEffect(() => {
    if (!selected.value) return;
    if (toNumber(local.MaxLoadingTimeSeconds.value === 0)) return;

    // Reset retry count when document URL changes
    retryCount.value = 0;
    isRetrying.value = false;

    const timeoutId = setTimeout(() => {
      loadingInterval.value = toNumber(local.MaxLoadingTimeSeconds.value);
    }, toNumber(local.MaxLoadingTimeSeconds.value) * 1000);

    // Cleanup function to clear the timeout on unmount
    return () => {
      clearTimeout(timeoutId);
    };
  });

  const isPdf = () => local.DocumentFormat.value === "pdf";

  const hasError = () =>
    (!local.NumPages.value && loadingInterval.value === toNumber(local.MaxLoadingTimeSeconds.value)) || (
      !local.NumPages.value &&
      !isRetrying.value &&
      retryCount.value >= maxRetries
    );

  const infiniteMode = () => local.InfiniteMode.value && !local.PreviewMode.value;

  const pagesRenderedPlusOne = Math.min(local.PagesRendered.value + 1, local.NumPages.value);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollHeight, scrollTop, clientHeight } = containerRef.current;

    if (scrollTop + clientHeight >= scrollHeight && local.PagesRendered.value <= pagesRenderedPlusOne) {
      local.PagesRendered.value += 1;
    }

    if (scrollHeight <= clientHeight && local.PagesRendered.value <= pagesRenderedPlusOne) {
      local.PagesRendered.value += 1;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY; // Current scroll position from top
      const windowHeight = window.innerHeight; // Viewport height
      const docHeight = document.documentElement.scrollHeight; // Total document height
      const scrollThreshold = docHeight * 0.9;

      if (scrollY + windowHeight >= scrollThreshold && local.PagesRendered.value <= pagesRenderedPlusOne) {
        // Add some threshold
        local.PagesRendered.value += 1;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [local.PagesRendered, pagesRenderedPlusOne]);

  const pageWidth = useWidth(documentRef, local.PreviewMode.value);

  const pagesToRender = local.LoadAllPage.value ? local.NumPages.value : pagesRenderedPlusOne;

  return (
    local.Visible.value && cookieValidated.value && (
      <Stack
        direction="column"
        ref={containerRef}
        data-testid={getTestId(config)}
        data-type={config.__typename}
        width="100%"
        position="relative"
        onMouseEnter={() => zoomRef.current?.style.setProperty("opacity", "1")}
        onMouseLeave={() => zoomRef.current?.style.setProperty("opacity", "0")}
        overflow={local.PreviewMode.value ? "hidden" : "auto"}
        onScroll={handleScroll}
      >
        {local.Documents.value.length > 1 && local.CurrentDocument.value > -1 && (
          <Stack
            direction="row"
            height={0}
            position="sticky"
            top="50%"
            alignItems="center"
            justifyContent="space-between"
            zIndex={3}
          >
            <Button
              onClick={goToPrevDocument}
              disabled={local.CurrentDocument.value === 0}
              className={getClassName(local.ButtonClasses)}
              sx={getsxObject(local.ButtonStyle.value, { cursor: "pointer" })}
              endIcon={
                <NGIcon
                  config={
                    {
                      IconName: "arrow_back_ios",
                      Style: { fontSize: local.NavigationButtonSize.value },
                    } as Icon
                  }
                  context={context}
                />
              }
            />
            <Button
              onClick={goToNextDocument}
              disabled={local.CurrentDocument.value === local.Documents.value.length - 1}
              className={getClassName(local.ButtonClasses)}
              sx={getsxObject(local.ButtonStyle.value, { cursor: "pointer" })}
              endIcon={
                <NGIcon
                  config={
                    {
                      IconName: "arrow_forward_ios",
                      Style: { fontSize: local.NavigationButtonSize.value },
                    } as Icon
                  }
                  context={context}
                />
              }
            />
          </Stack>
        )}
        {!local.PreviewMode.value && (
          <Stack
            direction="row"
            position="sticky"
            width="fit-content"
            top={config.ZoomVerticalPosition ?? "10px"}
            bgcolor="white"
            left={config.ZoomHorizontalPosition ?? "10%"}
            sx={{
              transform: "translate(-50%)",
              transition: "opacity ease-in-out .2s",
              opacity: 0,
            }}
            boxShadow="0 30px 40px 0 rgba(16, 36, 94, .2)"
            borderRadius="4px"
            border="1px solid #E0E0E0"
            zIndex={3}
            ref={zoomRef}
          >
            <Button
              onClick={zoomOut}
              disabled={local.Scale.value < 0.3}
              className={getClassName(local.AuxiliaryButtonClasses)}
              sx={getsxObject(local.AuxiliaryButtonStyle.value)}
            >
              <NGIcon config={{ IconName: "zoom_out" } as Icon} context={context} />
            </Button>
            <Button
              onClick={zoomIn}
              disabled={local.Scale.value > 5}
              className={getClassName(local.AuxiliaryButtonClasses)}
              sx={getsxObject(local.AuxiliaryButtonStyle.value)}
            >
              <NGIcon config={{ IconName: "zoom_in" } as Icon} context={context} />
            </Button>
          </Stack>
        )}
        <Box
          sx={getsxObject(local.Style.value, defaultStyle()) as SxProps<Theme>}
          className={getClassName(local.Classes, context)}
          onClick={handleOnClick}
        >
          {!isPdf() && (
            <NGImage
              config={{
                Id: generateUID(),
                Bindings: {
                  Value: config.FallbackImageUrl
                    ? `'${config.FallbackImageUrl}'`
                    : `'${local.DocumentFormat.value}'.toLowerCase() + '' + '.png'`,
                },
              }}
              context={context}
            />
          )}

          {isPdf() && hasError() && <Typography>{local.ErrorMessage.value}</Typography>}

          {isPdf() && !hasError() && !infiniteMode() && cookieValidated.value && Document && Page && (
            <Box
              ref={documentRef}
              width="calc(100% - 4rem)"
              marginLeft={local.PreviewMode.value ? undefined : "auto"}
              marginRight={local.PreviewMode.value ? undefined : "auto"}
              display={local.PreviewMode.value ? "block" : "flex"}
              justifyContent="center"
            >
              <Document
                key={`document-normal-${selected.value}-${retryCount.value}`}
                file={selected.value}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentError}
                error={(retryCount.value >= maxRetries || hasError()) ? local.ErrorMessage.value : <DocumentLoader />}
                loading={<DocumentLoader />}
                className={getClassName(local.Classes, context)}
                onMouseEnter={() => paginationRef.current?.style.setProperty("opacity", "1")}
                onMouseLeave={() => paginationRef.current?.style.setProperty("opacity", "0")}
              >
                <Page
                  pageNumber={local.CurrentPage.value}
                  scale={local.Scale.value}
                  renderAnnotationLayer={!local.PreviewMode.value}
                  renderTextLayer={!local.PreviewMode.value}
                  width={
                    config.DocumentMaxWidth ? Math.min(width * 0.9, config.DocumentMaxWidth) : pageWidth || width * 0.85
                  }
                />
                {!local.PreviewMode.value && (
                  <Stack
                    direction="row"
                    position="sticky"
                    width="fit-content"
                    bottom="5%"
                    bgcolor="white"
                    left="50%"
                    sx={{
                      transform: "translate(-50%)",
                      transition: "opacity ease-in-out .2s",
                      opacity: 0,
                    }}
                    boxShadow="0 30px 40px 0 rgba(16, 36, 94, .2)"
                    borderRadius="4px"
                    border="1px solid #E0E0E0"
                    zIndex={2}
                    ref={paginationRef}
                  >
                    <Button
                      onClick={goToPrevPage}
                      disabled={local.CurrentPage.value <= 1}
                      className={getClassName(local.AuxiliaryButtonClasses)}
                      sx={getsxObject(local.AuxiliaryButtonStyle.value)}
                    >
                      <NGIcon config={{ IconName: "chevron_left" } as Icon} context={context} />
                    </Button>
                    <Typography component="p" marginBlockStart="1rem" marginBlockEnd="1rem">
                      Page {local.CurrentPage.value} of {local.NumPages.value}
                    </Typography>
                    <Button
                      onClick={goToNextPage}
                      disabled={local.CurrentPage.value >= local.NumPages.value}
                      className={getClassName(local.AuxiliaryButtonClasses)}
                      sx={getsxObject(local.AuxiliaryButtonStyle.value)}
                    >
                      <NGIcon config={{ IconName: "chevron_right" } as Icon} context={context} />
                    </Button>
                  </Stack>
                )}
              </Document>
            </Box>
          )}

          {isPdf() && !hasError() && infiniteMode() && cookieValidated.value && Document && (
            <Box
              ref={documentRef}
              width="calc(100% - 4rem)"
              marginLeft="auto"
              marginRight="auto"
              display="flex"
              justifyContent="center"
            >
              <Document
                key={`document-infinite-${selected.value}-${retryCount.value}`}
                file={selected.value}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentError}
                error={(retryCount.value >= maxRetries || hasError()) ? local.ErrorMessage.value : <DocumentLoader />}
                loading={<DocumentLoader />}
                className={getClassName(local.Classes, context)}
              >
                <Stack
                  direction="column"
                  alignItems="center"
                  sx={getsxObject(config.InfiniteStyle, {
                    width: "fit-content",
                    gap: "1rem",
                    "&>.MuiBox-root": {
                      boxShadow:
                        "4px 4px 10px var(--md-sys-color-on-disabled), -4px -4px 10px var(--md-sys-color-on-disabled)",
                      borderRadius: "4px",
                      border: "1px solid var(--palette-grey-200)",
                    },
                  })}
                >
                  {Array.from(new Array(pagesToRender), (_, index) => {
                    return (
                      <Box key={index} width="fit-content">
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          scale={local.Scale.value}
                          width={
                            config.DocumentMaxWidth
                              ? Math.min(width * 0.9, config.DocumentMaxWidth)
                              : width > 1400
                                ? undefined
                                : pageWidth || width * 0.85
                          }
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </Document>
            </Box>
          )}
        </Box>
      </Stack>
    )
  );
}
